/**
 * this is a custom state client that interfaces with both our own node (TokkenR)
 * and run's state client (RunConnect) at the same time, for redundancy & resilience
 * it trusts both and uses the first one that answers
 * 
 * doc: https://run.network/docs/#api-reference-apis-state
 * 
 * TODO see if we can add Relay's state server too ?
 */

/*
 StateWrapper adds a lot of functionalities
 https://github.com/runonbitcoin/run-sdk/blob/main/lib/plugins/state-wrapper.js
*/

const Run = require("runlib")
const Promise_Any = require("p-any")

import AsyncRetry from "async-retry"
import ExpiryMap from "expiry-map"
import pMemoize from "p-memoize"
import { getLog } from "../helpers/helpers.js"
import pDebounce from "../helpers/pDebounce.js"

const StateWrapper = Run.plugins.StateWrapper


import TokkenrStateClient from "./TokkenrStateClient.js"
import { getSharedWhatsOnChainConnector } from "./WhatsOnChainConnector.js"
const tokkenrStateClient = new TokkenrStateClient()

const runStateClient = new Run.plugins.StateServer()



class CustomStateClient extends StateWrapper {

    // --------------------------------------------------------------------------
    // constructor
    // --------------------------------------------------------------------------

    /**
     * @param {?object} options Optional configurations options
     * @param {?string} options.network Network string. Defaults to main.
     */
    constructor(options = {}) {
        super()
        this.classname = "CustomStateClient: "
        this.network = _parseNetwork(options.network)
        const log = getLog(this.classname)

        this.DEBUG = true

        this.stateClients = [
            tokkenrStateClient,
            // runStateClient, // deprecated
        ]

        this.config = {
            use_memoize: true,
            use_retries: true,
            use_debounce: true,
        }
        log("this.config=", JSON.stringify(this.config))

        this.memoize_cache = new ExpiryMap(2 * 1000)  // cached values expire after X ms

        this.retry_config = {
            retries: 2,  // 2 means 2 retries so 3 calls in all
            minTimeout: 500,  // in ms
            maxTimeout: 5000,  // in ms
            factor: 2,  // exponential-backoff factor
        }

        if (this.DEBUG) log("this.stateClients=", this.stateClients)
    }


    // --------------------------------------------------------------------------
    // clear_cache
    // --------------------------------------------------------------------------

    /**
     * clears the in-memory memoize_cache (pull results).
     * call this right before a critical operation (ex: building & broadcasting a transaction)
     * to force fresh lookups instead of relying on cached results that could be up to
     * memoize_cache's TTL old.
     * note: this does NOT cancel any currently in-flight (debounced/retrying) calls,
     * it only discards already-resolved cached results so the NEXT call refetches.
     */
    clear_cache() {
        const log = getLog(this.classname, "clear_cache():")
        log("clearing memoize_cache (had", this.memoize_cache.size, "entries)")
        this.memoize_cache.clear()
    }


    // --------------------------------------------------------------------------
    // broadcast
    // --------------------------------------------------------------------------

    /**
     * @param {*} rawtx 
     * 
     * broadcast(rawtx: string): Promise<>
     * A notification for state providers when a new transaction should be indexed.
     * This method is optional for state providers.
     */
    async broadcast(rawtx) {
        const function_id = this.classname + "broadcast():"
        const log = getLog(function_id)

        // we already broadcast in CustomBlockchain but it never hurts to add it here too

        /*
        CustomStateClient: broadcast(): all stateClients failed AggregateError: 
        */

        // our goal is to talk with ALL state clients to increase reliability
        // and take the response from the FIRST one that fulfils it to increase speed		
        var allpromises = []
        this.stateClients.forEach(stateClient => {
            // had some: TypeError: stateClient.broadcast is not a function
            if (stateClient.broadcast) {
                allpromises.push(stateClient.broadcast(rawtx))
            }
        })

        try {
            const start_time = Date.now()
            log("broadcasting to", allpromises.length, "state clients...")
            await Promise_Any(allpromises)
            log("successful broadcast in", (Date.now() - start_time), "ms")
        } catch (e) {
            const errmess = function_id + " all stateClients failed " + e
            console.error(errmess)
            throw new Error(errmess, e)
        }
        if (this.DEBUG) log("success")
    }


    // --------------------------------------------------------------------------
    // locations
    // --------------------------------------------------------------------------

    /* BEWARE THE NODE DOES NOT INDEXES BSV ADDRESSES SO IF YOU TRY TO RETRIEVE
    UTXOS FROM A PURSE IT WILL ANSWER AN EMPTY ARRAY */
    async locations(script) {
        const function_id = this.classname + "locations():"
        const log = getLog(function_id)

        // our goal is to talk with ALL state clients to increase reliability
        // and take the response from the FIRST one that fulfils it to increase speed

        // beware tokkenr doesn't know utxos for purse addresses !
        let res_any
        try {
            const res_tokkenr = await tokkenrStateClient.locations(script)
            if (res_tokkenr && res_tokkenr.length > 0) {
                res_any = res_tokkenr
            } else {
                // if tokkenr is empty array it might be a purse address so we must ask other nodes
                res_any = await runStateClient.locations(script)
            }
        } catch (e) {
            const errmess = function_id + " all stateClients failed " + e
            console.error(errmess)
            throw new Error(errmess, e)
        }
        if (this.DEBUG) log("promises res_any=", typeof res_any, ": ", res_any)

        return res_any  // return the first successful one
    }


    // --------------------------------------------------------------------------
    // pull
    // --------------------------------------------------------------------------

    async pull(key, options) {
        const function_id = this.classname + "pull():"
        const log = getLog(function_id)

        // Bans are not pulled from Run-DB, because if Run-DB bans, then the jig state is also gone
        // cf https://github.com/runonbitcoin/run-sdk/blob/main/lib/plugins/run-db.js
        if (key.startsWith("ban://")) return

        if (!this.config.use_memoize)
            return await this._pull_debounced(key, options)

        // we must create it only once and then reutilize it for it to work
        if (!this.pull_memoized) {
            this.pull_memoized = pMemoize(this._pull_debounced, { cache: this.memoize_cache }).bind(this)
        }
        return await this.pull_memoized(key, options)
    }
    async _pull_debounced(key, options) {
        const log = getLog(this.classname + "_pull_debounced(): ")

        if (!this.config.use_debounce)
            return await this._pull_retry(key, options)

        // we must create it only once and then reutilize it for it to work
        // but me scope it to only this key
        if (!this.pull_debounced) this.pull_debounced = {}
        // if (this.DEBUG) log("before:", this.pull_debounced[key], "for key=", key)
        if (!this.pull_debounced[key]) {
            // pDebounce.promise will return the first result for all those that have been called in parallel
            this.pull_debounced[key] = pDebounce.promise(
                async () => { return await this._pull_retry(key, options) }
            ).bind(this)
        }

        return await this.pull_debounced[key]()
    }
    async _pull_retry(key, options) {
        const log = getLog(this.classname + "_pull_retry(): ")
        // if (this.DEBUG) log("with key=", key)

        if (!this.config.use_retries)
            return await this._pull(key, options)

        return await AsyncRetry(async () => {
            return await this._pull(key, options)
        }, this.retry_config)
    }
    async _pull(key, options) {
        const function_id = this.classname + "_pull():"
        const log = getLog(function_id)

        // Bans are not pulled from Run-DB, because if Run-DB bans, then the jig state is also gone
        // cf https://github.com/runonbitcoin/run-sdk/blob/main/lib/plugins/run-db.js
        if (key.startsWith("ban://")) return

        // if (this.DEBUG) log("with key=", key)

        // our goal is to talk with ALL state clients to increase reliability
        // and take the response from the FIRST one that fulfils it to increase speed		
        var allpromises = []
        this.stateClients.forEach(stateClient => {
            allpromises.push(stateClient.pull(key, options))
        })
        var res_any
        try {
            res_any = await Promise_Any(allpromises)
        } catch (e) {
            // all state clients rejected — but for tx:// keys we can still try WoC,
            // which has unconfirmed/mempool txs that the node hasn't indexed yet.
            // this is what makes broadcasts "invisible" when the node is down: the
            // RUN SDK can still load raw tx hex from WoC and reconstruct jig state,
            // so combinedtokens.sync() / remainingtokens.sync() succeed even though
            // the node returned 404 for the jig:// pull.
            if (key.startsWith("tx://")) {
                log("all stateClients failed for", key, "=> trying WoC fallback for raw tx hex")
                try {
                    res_any = await this._pull_tx_from_woc(key)
                } catch (e2) {
                    const errmess = function_id + " all stateClients failed for " + key + " and WoC fallback also failed: " + e2
                    console.error(errmess)
                    throw new Error(errmess, e2)
                }
            } else {
                const errmess = function_id + " all stateClients failed for " + key + ": " + e
                console.error(errmess)
                throw new Error(errmess, e)
            }
        }
        if (this.DEBUG) log("promises res_any=", typeof res_any, ": ", res_any, "for", key)

        // we return the first successful one
        return res_any
    }

    /**
     * WoC fallback for tx:// keys: fetches raw tx hex from WhatsOnChain's /tx/{txid}/hex
     * endpoint, which returns unconfirmed/mempool txs (unlike the node which only has
     * confirmed/indexed txs). The raw hex is enough for the RUN SDK to reconstruct jig
     * state by parsing the OP_RETURN metadata.
     */
    async _pull_tx_from_woc(key) {
        const function_id = this.classname + "_pull_tx_from_woc():"
        const log = getLog(function_id)
        const txid = key.replace("tx://", "")
        log("fetching raw tx hex from WoC for txid=", txid)
        const wocConnector = getSharedWhatsOnChainConnector()
        const rawHex = await wocConnector.fetchTxHex(txid)
        log("WoC returned", rawHex ? rawHex.length + " chars of hex" : "null", "for", txid)
        return rawHex
    }
}

// ------------------------------------------------------------------------------------------------
// Parameter validation
// ------------------------------------------------------------------------------------------------

function _parseNetwork(network) {
    if (typeof network === 'undefined') return 'main'
    if (typeof network !== 'string') throw new Error(`Invalid network: ${network}`)
    if (network !== 'main') {
        throw new Error(`CustomStateClient does not support the "${network}" network`)
    }
    return network
}

// ------------------------------------------------------------------------------------------------

export default CustomStateClient